<script setup>
import VHeroSection from '@/modules/Signup/components/VHeroSection.vue';
import VIconRender from '@/modules/Signup/components/VIconRender.vue';
import { nextTick, onMounted, ref, watch, watchEffect } from 'vue';
import VButton from '@/modules/Signup/components/VButton.vue';
import { VRadioButton } from '@/modules/Signup/components';
import VAddressSearch from '@/modules/Signup/components/address/VAddressSearch.vue';
import VPricingPlans from '@/modules/Signup/components/pricing-plans/VPricingPlans.vue';
import { useSignUpModuleStore } from '@/stores/modules/signup/signUpStore';
import { storeToRefs } from 'pinia/dist/pinia';
import { useRouter } from 'vue-router';
import { scrollToElement } from '@/utilities/scrollToElement';
import VSection from '@/modules/Signup/components/VSection.vue';
import PlanProductList from '@/modules/Signup/components/plan-products/PlanProductList.vue';
import { encryptAndStoreAccountData } from '@/modules/Signup/services/signupService';
import { useVLoader } from '@/modules/Signup/composables/useVLoader';
import VLoader from '@/modules/Signup/components/VLoader.vue';

const router = useRouter();
const
    signUpStore = useSignUpModuleStore(),
    {
        parentAccountId,
        selectedPlanId,
        selectedModemId, // todo: remove this and use instead 'selectedProducts'
        selectedPlan,
        selectedProducts,
        selectedNbnLocation,
        packagePlans,
        availableProducts,
        showPackagePlans,
    } = storeToRefs(signUpStore);

const { isLoading, loadingText, withLoading } = useVLoader();

onMounted(async () => {
    try {
        // Prepare all the generic API endpoints.
        await withLoading(async () => {
            await Promise.all([
                signUpStore.signUpLogin(),
                signUpStore.getBanks(),
            ]);
        }, 'Preparing, please wait ...');
    } catch (error) {
        console.error('Failed to initialize', error);
    }
});

/** Sections */
const showSection = ref({
    modem: false,
});

const
    planSection = ref(null),
    modemSection = ref(null);

async function scrollToModemSection() {
    showSection.value.modem = true; // Handles the scroll through `watch`
}

watch(
    () => showSection.value.modem,
    async (newVal) => {
        if (newVal) {
            await nextTick();
            scrollToElement(modemSection.value);
        }
    },
);

const handleSelectedPlan = (data) => {
    selectedPlanId.value = data.ID;
    selectedPlan.value = data;
    availableProducts.value = data.AvailableProducts?.Product;
};

// TODO: remove
watch(selectedPlanId, () => {
    // When user's select another plan, reset the modem selection.
    selectedModemId.value = null;
});

watch(
    () => selectedPlan.value.ID,
    () => {
        // When user's select another plan, reset the modem selection.
        selectedProducts.value = {};
    },
);

const handleSelectedModem = (data) => {
    selectedModemId.value = data.ID;
    selectedProducts.value = data;
};

const selectedModemOption = ref(null);
const modemOptions = ref([
    { id: true, name: 'I already have one' },
    { id: 'custom', name: 'Choose a new modem' },
]);

const validateBeforeRedirection = async () => {
    if (
        !selectedPlanId.value ||
        !selectedModemOption.value ||
        (selectedModemOption.value?.id === 'custom' && !selectedModemId.value)
    ) {
        // TODO: Create a toast or something that will allow users to see the required fields before proceeding.
        alert('Please choose a modem option!');
        return;
    }

    // Contains the first step's data, which will be encrypted and stored.
    const accountData = {
        parentAccountId: parentAccountId.value,
        planId: selectedPlanId.value,
        modemId: selectedModemId.value,
        // req payload for perform-simple-nbn-sq
        directoryID: selectedNbnLocation.value,
    };

    // Encrypt and store the account data
    const encryptSuccess = await encryptAndStoreAccountData(accountData);

    if (!encryptSuccess) {
        alert('Error storing account data. Please try again.');
        return;
    }

    // Continue with the redirection
    await router.push({ name: 'signup' });
};

watchEffect(async () => {
    /** load available packages */
    if (showPackagePlans.value) {
        const { operation, loadingText } = await signUpStore.getAvailablePackages();
        await withLoading(operation, loadingText);
    }
});
</script>

<template>
    <VHeroSection>
        <h2 class="tw-text-white !tw-text-[4.8em]">
            Plans &amp; Pricing
            <div class="tw-flex tw-items-center tw-gap-x-5">
                made
                <img
                    :src="require('@/modules/Signup/__clients/ezi/logo.svg')"
                    class="!tw-h-[0.85em] tw-mb-1"
                    alt="logo"
                />
            </div>
        </h2>
    </VHeroSection>

    <div class="tw-w-2/3 tw-mx-auto tw-my-16">
        <h4 class="tw-text-primary !tw-text-[2em] !tw-font-semibold">
            Faster internet with better valued plans. It’s that
            <VIconRender
                :icon-src="require('@/modules/Signup/__clients/ezi/logo-2.svg')"
                class="tw-text-primary tw-inline-flex"
            />
        </h4>
    </div>

    <div class="tw-flex tw-flex-col tw-gap-20">
        <div class="tw-w-2/3 tw-mx-auto">
            <h4 class="tw-text-secondary !tw-text-[2em] tw-font-neue-kabel-bold tw-flex tw-items-center !tw-mb-6">
                Let's find out what's available to you
            </h4>

            <!-- This contains the entire search box functionalities and behaviours -->
            <VAddressSearch />
        </div>

        <!-- Choose your plan-->
        <VSection v-if="showPackagePlans" title="Choose your plan">
            <div ref="planSection">
                <VPricingPlans
                    :plans="packagePlans"
                    :selectedPlanId="selectedPlanId"
                    @selectPlan="handleSelectedPlan"
                />
            </div>

            <template #footer>
                <VButton label="Next" @click="scrollToModemSection" />
            </template>
        </VSection>

        <!-- Modem-->
        <VSection v-if="showSection.modem" title="Would you like to add a modem?">
            <div ref="modemSection">
                <VRadioButton
                    v-model="selectedModemOption"
                    :options="modemOptions"
                    list-item-class="tw-font-semibold"
                    class="tw-w-2/3 tw-mx-auto"
                >
                    <template #option-true>
                        <ul class="tw-flex tw-flex-col tw-gap-5 tw-text-[1.2em] !tw-mt-1 !tw-mb-6 tw-w-11/12">
                            <li class="tw-font-normal tw-flex tw-gap-2">
                                <i class="pi pi-check tw-mt-3"></i>
                                Information for the criteria that the modem must meet in order to function with the new
                                plan
                            </li>
                            <li class="tw-font-normal tw-flex tw-gap-2">
                                <i class="pi pi-check tw-mt-3"></i>
                                Another piece of information for the criteria that the modem must meet in order to
                                function with the new plan
                            </li>
                        </ul>
                    </template>
                </VRadioButton>

                <PlanProductList
                    :plans="availableProducts"
                    :selectedPlanId="selectedModemId"
                    @selectPlanProduct="handleSelectedModem"
                    :disabled="selectedModemOption?.id !== 'custom'"
                    class="tw-mt-6"
                />
            </div>
            <template #footer>
                <VButton label="Next" @click="validateBeforeRedirection" />
            </template>
        </VSection>
    </div>

    <!-- Show loader during route transitions -->
    <VLoader
        v-if="isLoading"
        :text="loadingText"
    />
</template>
